import { Simulation } from '@/hooks/useSimulation';
import { Beneficiary } from '@/types/beneficiary';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { IOFAmount } from '@/utility/IOFAmount';
import { GenericAction } from '@/utility/reducers';

export interface DepositFlowStateBase {
  readonly selectedFundingMethod: PaymentRailType;
  readonly fundingMethods: readonly PaymentRailType[];
  readonly fromAmount: number | null /* null means: unset */;
  readonly fromCurrency: CurrencyCodeType;
  readonly toAmount: number | null;
  readonly toCurrency: CurrencyCodeType;
  readonly simulationId: string | null;
  readonly reviewRevealed: boolean;
  readonly selectedBeneficiaryId: string;
  readonly beneficiaries: readonly Beneficiary[];
  readonly mainBeneficiaryId: string;
  readonly senderType: 'myself' | 'customer' | null;
  readonly markupBps: number | null;
  readonly amountInputSide: 'from' | 'to' | null;
}

export interface PIXDepositFlowState extends DepositFlowStateBase {
  readonly IOF?: {
    readonly label: string;
    readonly amount: IOFAmount;
  };
}

export interface SPEIDepositFlowState extends DepositFlowStateBase {
  readonly recipientName: string;
  readonly clabe: string;
  readonly accountNumber: string;
  readonly institution: string;
}

export interface ACHOrWireDepositFlowState extends DepositFlowStateBase {
  readonly beneficiaryName: string;
  readonly accountType: 'checking' | 'savings';
  readonly bankName: string;
  readonly bankAddress: string;
  readonly accountNumber: string;
  readonly routingNumber: string;
}

export interface CryptoDepositFlowState extends DepositFlowStateBase {
  readonly chain: string;
  readonly address: string;
}

export type DepositFlowState =
  | PIXDepositFlowState
  | SPEIDepositFlowState
  | ACHOrWireDepositFlowState
  | CryptoDepositFlowState;

export const initialState: DepositFlowState = {
  fundingMethods: [],
  selectedFundingMethod: PaymentRailType.unset,
  fromAmount: null,
  fromCurrency: CurrencyCodeType.UNSET,
  toAmount: null,
  toCurrency: CurrencyCodeType.UNSET,
  simulationId: null,
  reviewRevealed: false,
  selectedBeneficiaryId: '',
  beneficiaries: [],
  mainBeneficiaryId: '',
  senderType: null,
  markupBps: null,
  amountInputSide: null,
};

export enum DepositFlowActionType {
  transactionFlowChanged = 'transactionFlowChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  simulationCreated = 'simulationCreated',
  depositFlowFinished = 'depositFlowFinished',
  fundingMethodChanged = 'fundingMethodChanged',
  beneficiaryChanged = 'beneficiaryChanged',
  beneficiariesLoaded = 'beneficiariesLoaded',
  mainBeneficiarySet = 'mainBeneficiarySet',
  senderTypeChanged = 'senderTypeChanged',
  toCurrencyChanged = 'toCurrencyChanged',
  markupBpsChanged = 'markupBpsChanged',
}

export type FundingMethodChangedAction = GenericAction<
  DepositFlowActionType.fundingMethodChanged,
  PaymentRailType
>;

export type TransactionFlowChangedAction = GenericAction<
  DepositFlowActionType.transactionFlowChanged,
  DashboardOperation
>;

export type ToAmountChangedAction = GenericAction<
  DepositFlowActionType.toAmountChanged,
  number | null
>;

export type FromAmountChangedAction = GenericAction<
  DepositFlowActionType.fromAmountChanged,
  number | null
>;

export type SimulationCreatedAction = GenericAction<
  DepositFlowActionType.simulationCreated,
  Simulation
>;

export type ResetDepositFlowAction = GenericAction<DepositFlowActionType.depositFlowFinished, void>;

export type BeneficiaryChangedAction = GenericAction<
  DepositFlowActionType.beneficiaryChanged,
  string
>;

export type BeneficiariesLoadedAction = GenericAction<
  DepositFlowActionType.beneficiariesLoaded,
  readonly Beneficiary[]
>;

export type MainBeneficiarySetAction = GenericAction<
  DepositFlowActionType.mainBeneficiarySet,
  string
>;

export type SenderTypeChangedAction = GenericAction<
  DepositFlowActionType.senderTypeChanged,
  'myself' | 'customer' | null
>;

export type ToCurrencyChangedAction = GenericAction<
  DepositFlowActionType.toCurrencyChanged,
  CurrencyCodeType
>;

export type MarkupBpsChangedAction = GenericAction<
  DepositFlowActionType.markupBpsChanged,
  number | null
>;

export type DepositFlowAction =
  | TransactionFlowChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | SimulationCreatedAction
  | ResetDepositFlowAction
  | FundingMethodChangedAction
  | BeneficiaryChangedAction
  | BeneficiariesLoadedAction
  | MainBeneficiarySetAction
  | SenderTypeChangedAction
  | ToCurrencyChangedAction
  | MarkupBpsChangedAction;

export const depositFlowReducer = (
  state: DepositFlowState,
  action: DepositFlowAction,
): DepositFlowState => {
  switch (action.type) {
    case DepositFlowActionType.transactionFlowChanged: {
      const { currency, destinations } = action.payload;

      // Failsafe only
      if (destinations.length === 0) {
        return initialState;
      }

      const destinationCurrency = destinations[0].currency;

      const fundingMethods = [
        ...new Set(
          destinations.flatMap(
            (destination: DashboardOperationDestination): readonly PaymentRailType[] =>
              destination.fundingMethods ?? [],
          ),
        ),
      ];

      return {
        ...state,
        fromCurrency: currency,
        toCurrency: destinationCurrency,
        fundingMethods,
        selectedFundingMethod:
          fundingMethods.length === 1 ? fundingMethods[0] : PaymentRailType.unset,
        reviewRevealed: false,
      };
    }
    case DepositFlowActionType.toAmountChanged: {
      const amount = action.payload;

      if (amount !== state.toAmount) {
        return {
          ...state,
          toAmount: amount,
          simulationId: null,
          fromAmount: null,
          amountInputSide: 'to',
        };
      }

      return state;
    }
    case DepositFlowActionType.fromAmountChanged: {
      const amount = action.payload;

      if (amount !== state.fromAmount) {
        return {
          ...state,
          toAmount: null,
          simulationId: null,
          fromAmount: amount,
          amountInputSide: 'from',
        };
      }

      return state;
    }
    case DepositFlowActionType.simulationCreated: {
      const { id, to, from } = action.payload;
      return {
        ...state,
        simulationId: id,
        fromAmount: state.fromAmount ?? from.value,
        toAmount: state.toAmount ?? to.value,
        reviewRevealed: !!id || state.reviewRevealed,
      };
    }
    case DepositFlowActionType.fundingMethodChanged:
      return {
        ...state,
        selectedFundingMethod: action.payload,
        toAmount: null,
        simulationId: null,
        reviewRevealed: false,
      };
    case DepositFlowActionType.depositFlowFinished: {
      return {
        ...state,
        selectedFundingMethod: PaymentRailType.unset,
        fromAmount: null,
        fromCurrency: CurrencyCodeType.UNSET,
        toAmount: null,
        toCurrency: CurrencyCodeType.UNSET,
        simulationId: null,
        reviewRevealed: false,
        markupBps: null,
      };
    }
    case DepositFlowActionType.beneficiaryChanged:
      return { ...state, selectedBeneficiaryId: action.payload };
    case DepositFlowActionType.beneficiariesLoaded:
      return { ...state, beneficiaries: action.payload };
    case DepositFlowActionType.mainBeneficiarySet:
      return { ...state, mainBeneficiaryId: action.payload };
    case DepositFlowActionType.senderTypeChanged:
      return { ...state, senderType: action.payload };
    case DepositFlowActionType.toCurrencyChanged:
      return {
        ...state,
        toCurrency: action.payload,
        toAmount: null,
        simulationId: null,
      };
    case DepositFlowActionType.markupBpsChanged:
      return {
        ...state,
        markupBps: action.payload,
        fromAmount: state.amountInputSide === 'to' ? null : state.fromAmount,
        toAmount: state.amountInputSide === 'to' ? state.toAmount : null,
      };

    default:
      return state;
  }
};

export const transactionFlowChanged = (
  payload: TransactionFlowChangedAction['payload'],
): TransactionFlowChangedAction => ({
  type: DepositFlowActionType.transactionFlowChanged,
  payload: payload,
});

export const fromAmountChanged = (
  payload: FromAmountChangedAction['payload'],
): FromAmountChangedAction => ({
  type: DepositFlowActionType.fromAmountChanged,
  payload: payload,
});

export const toAmountChanged = (
  payload: ToAmountChangedAction['payload'],
): ToAmountChangedAction => ({
  type: DepositFlowActionType.toAmountChanged,
  payload: payload,
});

export const simulationCreated = (
  payload: SimulationCreatedAction['payload'],
): SimulationCreatedAction => ({
  type: DepositFlowActionType.simulationCreated,
  payload: payload,
});

export const depositFlowFinished = (): ResetDepositFlowAction => ({
  type: DepositFlowActionType.depositFlowFinished,
  payload: undefined,
});

export const fundingMethodChanged = (
  fundingMethod: PaymentRailType,
): FundingMethodChangedAction => ({
  type: DepositFlowActionType.fundingMethodChanged,
  payload: fundingMethod,
});

export const beneficiaryChanged = (beneficiaryId: string): BeneficiaryChangedAction => ({
  type: DepositFlowActionType.beneficiaryChanged,
  payload: beneficiaryId,
});

export const beneficiariesLoaded = (
  beneficiaries: readonly Beneficiary[],
): BeneficiariesLoadedAction => ({
  type: DepositFlowActionType.beneficiariesLoaded,
  payload: beneficiaries,
});

export const mainBeneficiarySet = (beneficiaryId: string): MainBeneficiarySetAction => ({
  type: DepositFlowActionType.mainBeneficiarySet,
  payload: beneficiaryId,
});

export const senderTypeChanged = (
  senderType: 'myself' | 'customer' | null,
): SenderTypeChangedAction => ({
  type: DepositFlowActionType.senderTypeChanged,
  payload: senderType,
});

export const toCurrencyChanged = (payload: CurrencyCodeType): ToCurrencyChangedAction => ({
  type: DepositFlowActionType.toCurrencyChanged,
  payload: payload,
});

export const markupBpsChanged = (payload: number | null): MarkupBpsChangedAction => ({
  type: DepositFlowActionType.markupBpsChanged,
  payload: payload,
});
